import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PlatformCardListRow } from '../../../../core/models/card-quota/platform-card.model';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { MapperPlatformCardService } from '../../../../core/mappers/card-quota/mapper-platform-card.service';
import { ApiCardQuotaService } from '../../../../core/services/api/card-quota/api-card-quota.service';
import { ClientAccessScopeService } from '../../../../core/services/client-access-scope.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ModalParkingCardDetailComponent } from '../../modals/parking/parking-card/modal-parking-card-detail/modal-parking-card-detail.component';

type ModalMode = 'quota-create' | 'quota-edit' | 'view';
type ModalTab = 'userInfo' | 'accessInfo' | 'vehicles' | 'renewals';
type SearchField = 'card_name' | 'name' | 'client' | 'card_type';

@Component({
  selector: 'app-platform-cards',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, PssConfirmModalComponent, ModalParkingCardDetailComponent],
  templateUrl: './platform-cards.component.html',
  styleUrl: './platform-cards.component.css',
})
export class PlatformCardsComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  readonly clientScope = inject(ClientAccessScopeService);
  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperPlatformCardService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly permissionService = inject(MenuPermissionService);

  private static readonly CLIENT_OPTIONS_PREVIEW = 20;

  items = signal<PlatformCardListRow[]>([]);
  isLoading = signal(false);
  isModalOpen = signal(false);
  isRevokeConfirmOpen = signal(false);
  revokeTarget = signal<PlatformCardListRow | null>(null);
  filterStatus = signal('');

  modalMode: ModalMode = 'quota-create';
  selectedCardId: number | null = null;
  modalInitialTab: ModalTab = 'userInfo';

  searchField = signal<SearchField>('card_name');
  searchText = signal('');

  filterClientId = 0;
  clientSearchText = '';
  isClientDropdownOpen = false;

  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];
  totalItems = signal(0);
  totalPages = signal(0);

  readonly statusOptions = [
    { value: '', label: 'ทุกสถานะ' },
    { value: 'active', label: 'ใช้งาน' },
    { value: 'not_ready', label: 'ระงับ' },
    { value: 'revoked', label: 'ยกเลิกแล้ว' },
  ];

  readonly searchFieldOptions: { value: SearchField; label: string }[] = [
    { value: 'card_name', label: 'เลขบัตร' },
    { value: 'name', label: 'ชื่อผู้ถือบัตร' },
    { value: 'client', label: 'ร้าน' },
    { value: 'card_type', label: 'ประเภทบัตร' },
  ];

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) return;
      untracked(() => {
        this.currentPage.set(1);
        this.loadScopeAndTable();
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get canDelete(): boolean {
    return this.permissionService.canDelete(this.router.url);
  }

  get issueUserId(): number {
    return Number(this.cookieService.get('user_id') || 0);
  }

  get initialClientIdForModal(): number {
    if (this.filterClientId > 0) return this.filterClientId;
    return this.clientScope.lockedClientId();
  }

  get clientFilterOptions(): ClientIInfo[] {
    const clients = this.clientScope.state()?.clients ?? [];
    const sorted = [...clients].sort((a, b) => a.name.localeCompare(b.name, 'th'));
    const q = this.clientSearchText.trim().toLowerCase();
    if (!q) return sorted.slice(0, PlatformCardsComponent.CLIENT_OPTIONS_PREVIEW);
    return sorted.filter((c) => c.name.toLowerCase().includes(q));
  }

  statusLabel = (s: string) => this.mapper.statusLabel(s);

  isCardEditable(item: PlatformCardListRow): boolean {
    if (item.platformCardStatus === 'revoked' || item.platformCardStatus === 'not_ready') {
      return false;
    }
    if (item.quotaStatus === 'suspended') {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadScopeAndTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadScopeAndTable(true);
  }

  onSiteChange(siteId: number): void {
    const site = this.siteState.sites().find((s) => s.id === Number(siteId));
    if (!site) return;
    this.siteState.setSelectedSite(site);
  }

  onFilterChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  clearSearchText(): void {
    this.searchText.set('');
    this.onSearch();
  }

  openClientDropdown(): void {
    this.isClientDropdownOpen = true;
    if (this.filterClientId > 0 && !this.clientSearchText.trim()) {
      const selected = this.clientFilterOptions.find((c) => c.id === this.filterClientId);
      if (selected) this.clientSearchText = selected.name;
    }
  }

  closeClientDropdown(): void {
    this.isClientDropdownOpen = false;
    if (this.filterClientId > 0) {
      const clients = this.clientScope.state()?.clients ?? [];
      const selected = clients.find((c) => c.id === this.filterClientId);
      if (selected) this.clientSearchText = selected.name;
    }
  }

  onClientSearchInput(): void {
    this.isClientDropdownOpen = true;
  }

  selectClient(client: ClientIInfo): void {
    this.filterClientId = client.id;
    this.clientSearchText = client.name;
    this.isClientDropdownOpen = false;
    this.onSearch();
  }

  clearClientFilter(): void {
    this.filterClientId = 0;
    this.clientSearchText = '';
    this.isClientDropdownOpen = false;
    this.onSearch();
  }

  openIssue(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    if (this.clientScope.isRestricted() && this.clientScope.state()?.clients.length === 0) {
      this.toast.error('ไม่มีร้านที่คุณเข้าถึงได้ในลานนี้');
      return;
    }
    this.modalMode = 'quota-create';
    this.selectedCardId = null;
    this.modalInitialTab = 'userInfo';
    this.isModalOpen.set(true);
  }

  openEdit(item: PlatformCardListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isCardEditable(item)) {
      this.toast.error(
        item.platformCardStatus === 'not_ready' || item.quotaStatus === 'suspended'
          ? 'บัตรถูกระงับ — ไม่สามารถแก้ไขได้'
          : 'บัตรถูกยกเลิกแล้ว — ไม่สามารถแก้ไขได้'
      );
      return;
    }
    this.modalMode = 'quota-edit';
    this.selectedCardId = item.id;
    this.modalInitialTab = 'userInfo';
    this.isModalOpen.set(true);
  }

  openVehicles(item: PlatformCardListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isCardEditable(item)) {
      this.toast.error(
        item.platformCardStatus === 'not_ready' || item.quotaStatus === 'suspended'
          ? 'บัตรถูกระงับ — ไม่สามารถแก้ไขได้'
          : 'บัตรถูกยกเลิกแล้ว'
      );
      return;
    }
    this.modalMode = 'quota-edit';
    this.selectedCardId = item.id;
    this.modalInitialTab = 'vehicles';
    this.isModalOpen.set(true);
  }

  openView(item: PlatformCardListRow): void {
    this.modalMode = 'view';
    this.selectedCardId = item.id;
    this.modalInitialTab = 'userInfo';
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedCardId = null;
  }

  onCardSaved(): void {
    this.closeModal();
    this.loadScopeAndTable();
  }

  revokeCard(item: PlatformCardListRow, event?: Event): void {
    event?.stopPropagation();
    if (!this.canDelete) {
      this.toast.error('ไม่มีสิทธิ์ลบ/ยกเลิก');
      return;
    }
    if (item.platformCardStatus === 'revoked') {
      this.toast.error('บัตรถูกยกเลิกแล้ว');
      return;
    }
    this.revokeTarget.set(item);
    this.isRevokeConfirmOpen.set(true);
  }

  closeRevokeConfirm(): void {
    this.isRevokeConfirmOpen.set(false);
    this.revokeTarget.set(null);
  }

  confirmRevokeCard(): void {
    const item = this.revokeTarget();
    if (!item) return;

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url);
    const payload = this.mapper.buildRevokePayload(item.id, userId, menuId);

    this.closeRevokeConfirm();
    this.isLoading.set(true);

    this.api.revokePlatformParkingCard(payload).subscribe({
      next: (res) => {
        if (res.code === '000' || res.isSuccess) {
          this.toast.success(res.message || 'ยกเลิกบัตรสำเร็จ');
          this.loadTable();
        } else {
          this.toast.error(res.message || 'ยกเลิกบัตรล้มเหลว');
          this.isLoading.set(false);
        }
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'ยกเลิกบัตรล้มเหลว');
        this.isLoading.set(false);
      },
    });
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  onPageSizeChange(event: Event): void {
    const element = event.target as HTMLSelectElement;
    this.pageSize.set(Number(element.value));
    this.currentPage.set(1);
    this.loadTable();
  }

  trackById(_index: number, item: PlatformCardListRow): number {
    return item.id;
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  loadScopeAndTable(forceScope = false): void {
    const siteId = this.selectedSiteId;
    const userId = this.issueUserId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.clientScope.load(siteId, userId, forceScope).subscribe({
      next: () => this.loadTable(),
      error: () => this.loadTable(),
    });
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      this.isLoading.set(false);
      return;
    }

    this.isLoading.set(true);
    const search = this.searchText().trim();
    this.api
      .getPlatformCardsForTable(
        siteId,
        this.currentPage(),
        this.pageSize(),
        this.filterClientId > 0 ? this.filterClientId : null,
        this.filterStatus() || null,
        search ? this.searchField() : null,
        search || null
      )
      .subscribe({
        next: (res) => {
          const mapped = this.mapper.mapTable(res);
          this.items.set(mapped.items);
          this.totalItems.set(mapped.total);
          this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
          this.isLoading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.isLoading.set(false);
          this.toast.error('โหลดรายการบัตรล้มเหลว');
        },
      });
  }
}
